'use client';

/**
 * Entity Grid Component
 *
 * Flexible grid for displaying entities (apps, subscriptions, projects, etc.)
 * with status indicators and metrics
 *
 * @example
 * // Mothership - Apps overview
 * <EntityGrid
 *   title="Your Universe"
 *   subtitle="Cross-app intelligence at a glance"
 *   items={apps.map(app => ({
 *     id: app.id,
 *     title: app.name,
 *     subtitle: app.url,
 *     status: 'operational',
 *     metrics: [
 *       { label: 'API', value: '1.2K' },
 *       { label: 'Users', value: '234' },
 *     ],
 *     href: `/dashboard/apps/${app.slug}`,
 *   }))}
 * />
 *
 * // Repetix - Subscriptions
 * <EntityGrid
 *   title="Your Subscriptions"
 *   items={subscriptions.map(sub => ({
 *     id: sub.id,
 *     title: sub.name,
 *     subtitle: sub.provider,
 *     metrics: [{ label: 'Cost', value: '$9.99/mo' }],
 *   }))}
 * />
 */

import React from 'react';
import { useRouter } from 'next/navigation';
import { ExternalLink, LucideIcon } from 'lucide-react';

export type EntityStatus = 'operational' | 'degraded' | 'down' | 'maintenance' | 'active' | 'inactive' | 'pending';

export interface EntityMetric {
  label: string;
  value: string | number;
  color?: 'default' | 'success' | 'warning' | 'danger';
}

export interface EntityItem {
  id: string;
  title: string;
  subtitle?: string;
  icon?: LucideIcon | string;
  status?: EntityStatus;
  statusLabel?: string;
  metrics?: EntityMetric[];
  href?: string;
  externalUrl?: string;
  onClick?: () => void;
}

export interface EntityGridProps {
  title?: string;
  subtitle?: string;
  items: EntityItem[];
  columns?: 2 | 3 | 4;
  onViewAll?: () => void;
  emptyState?: {
    icon?: string;
    title: string;
    description: string;
    actionLabel?: string;
    onAction?: () => void;
  };
}

const statusConfig: Record<EntityStatus, { dot: string; label: string; labelColor: string }> = {
  operational: { dot: 'bg-green-400', label: 'OK', labelColor: 'text-green-400' },
  active: { dot: 'bg-green-400', label: 'Active', labelColor: 'text-green-400' },
  degraded: { dot: 'bg-yellow-400 animate-pulse', label: 'Degraded', labelColor: 'text-yellow-400' },
  pending: { dot: 'bg-yellow-400', label: 'Pending', labelColor: 'text-yellow-400' },
  down: { dot: 'bg-red-400 animate-pulse', label: 'Down', labelColor: 'text-red-400' },
  inactive: { dot: 'bg-slate-400', label: 'Inactive', labelColor: 'text-slate-400' },
  maintenance: { dot: 'bg-blue-400', label: 'Maint.', labelColor: 'text-blue-400' },
};

const metricColors = {
  default: 'text-white',
  success: 'text-green-400',
  warning: 'text-yellow-400',
  danger: 'text-red-400',
};

// Generate gradient based on title
const gradients = [
  'from-violet-500 to-purple-600',
  'from-blue-500 to-cyan-500',
  'from-orange-500 to-pink-500',
  'from-green-500 to-emerald-500',
  'from-rose-500 to-red-500',
  'from-amber-500 to-yellow-500',
];

function EntityIcon({ title, icon }: { title: string; icon?: LucideIcon | string }) {
  if (typeof icon === 'string') {
    return (
      <div className="h-10 w-10 rounded-lg bg-slate-800 border border-white/10 overflow-hidden">
        <img src={icon} alt={title} className="h-full w-full object-cover" />
      </div>
    );
  }

  if (icon) {
    const Icon = icon;
    return (
      <div className="h-10 w-10 rounded-lg bg-slate-800/80 border border-white/10 flex items-center justify-center">
        <Icon className="h-5 w-5 text-white/70" />
      </div>
    );
  }

  // Fallback: gradient with first letter
  const gradientIndex = title.charCodeAt(0) % gradients.length;

  return (
    <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${gradients[gradientIndex]} flex items-center justify-center`}>
      <span className="text-white font-bold text-lg">{title.charAt(0)}</span>
    </div>
  );
}

function EntityCard({ item }: { item: EntityItem }) {
  const router = useRouter();
  const config = item.status ? statusConfig[item.status] : null;

  const handleClick = () => {
    if (item.onClick) {
      item.onClick();
    } else if (item.href) {
      router.push(item.href);
    }
  };

  const isClickable = item.onClick || item.href;

  return (
    <button
      type="button"
      onClick={isClickable ? handleClick : undefined}
      className={`w-full text-left bg-slate-900/60 backdrop-blur-lg border border-white/10 rounded-xl p-4 transition-all group ${
        isClickable ? 'hover:border-white/20 hover:bg-slate-900/80 cursor-pointer' : ''
      }`}
      disabled={!isClickable}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <EntityIcon title={item.title} icon={item.icon} />
          <div>
            <h3 className={`text-sm font-semibold text-white ${isClickable ? 'group-hover:text-violet-400' : ''} transition-colors`}>
              {item.title}
            </h3>
            {item.subtitle && (
              item.externalUrl ? (
                <a
                  href={item.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs text-white/40 hover:text-violet-400 flex items-center gap-1 transition-colors"
                >
                  <span className="truncate max-w-[120px]">{item.subtitle}</span>
                  <ExternalLink className="h-3 w-3 flex-shrink-0" />
                </a>
              ) : (
                <p className="text-xs text-white/40 truncate max-w-[150px]">{item.subtitle}</p>
              )
            )}
          </div>
        </div>

        {/* Status badge */}
        {config && (
          <div className="flex items-center gap-1.5">
            <div className={`h-2 w-2 rounded-full ${config.dot}`} />
            <span className={`text-xs font-medium ${config.labelColor}`}>
              {item.statusLabel || config.label}
            </span>
          </div>
        )}
      </div>

      {/* Metrics row */}
      {item.metrics && item.metrics.length > 0 && (
        <div className={`grid gap-2 ${
          item.metrics.length === 1 ? 'grid-cols-1' :
          item.metrics.length === 2 ? 'grid-cols-2' :
          'grid-cols-3'
        }`}>
          {item.metrics.map((metric, index) => (
            <div key={metric.label + index} className="bg-slate-800/50 rounded-lg px-2 py-1.5">
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">{metric.label}</div>
              <div className={`text-sm font-semibold ${metricColors[metric.color || 'default']}`}>
                {metric.value}
              </div>
            </div>
          ))}
        </div>
      )}
    </button>
  );
}

function EmptyState({ config }: { config: NonNullable<EntityGridProps['emptyState']> }) {
  return (
    <div className="col-span-full bg-slate-900/40 border border-dashed border-white/20 rounded-xl p-8 text-center">
      {config.icon && (
        <div className="h-12 w-12 rounded-xl bg-violet-500/20 flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">{config.icon}</span>
        </div>
      )}
      <h3 className="text-lg font-semibold text-white mb-2">{config.title}</h3>
      <p className="text-sm text-white/60 mb-4 max-w-md mx-auto">
        {config.description}
      </p>
      {config.actionLabel && config.onAction && (
        <button
          type="button"
          onClick={config.onAction}
          className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium text-sm transition-colors"
        >
          {config.actionLabel}
        </button>
      )}
    </div>
  );
}

export function EntityGrid({ title, subtitle, items, columns = 3, onViewAll, emptyState }: EntityGridProps) {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className="space-y-4">
      {(title || subtitle || onViewAll) && (
        <div className="flex items-center justify-between">
          <div>
            {title && <h2 className="text-lg font-semibold text-white">{title}</h2>}
            {subtitle && <p className="text-sm text-white/50">{subtitle}</p>}
          </div>
          {onViewAll && (
            <button
              type="button"
              onClick={onViewAll}
              className="text-xs text-white/50 hover:text-white/80 transition-colors"
            >
              View All →
            </button>
          )}
        </div>
      )}

      <div className={`grid gap-4 ${gridCols[columns]}`}>
        {items.length === 0 && emptyState ? (
          <EmptyState config={emptyState} />
        ) : (
          items.map((item) => (
            <EntityCard key={item.id} item={item} />
          ))
        )}
      </div>
    </div>
  );
}
